import { startCase } from "lodash";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { FaCircleArrowLeft } from "react-icons/fa6";
import { RxCross2 } from "react-icons/rx";
import { MdKeyboardArrowLeft } from "react-icons/md";
import { MdKeyboardArrowRight } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import { apiGet } from "utils/apiFetch";
import apiPath from "utils/apiPath";
import { useTranslation } from "react-i18next";
import OSearch from "components/reusable/OSearch";

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}
function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}
function addMonths(date, n) {
  return new Date(date.getFullYear(), date.getMonth() + n, 1);
}
function formatISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export default function SPDriverAvailabilityCalendar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const childRef = useRef();
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [visibleMonth, setVisibleMonth] = useState(startOfMonth(today));
  const [loading, setLoading] = useState(false);
  const [monthData, setMonthData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTab, setSelectedTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    loadMonth(visibleMonth);
  }, [visibleMonth]);

  const handleReset = () => {
    setSearchQuery("");
    setSelectedDate(null);
    childRef?.current?.onClose();
  };

  const loadMonth = async (date) => {
    setLoading(true);
    try {
      const data = await fetchAvailabilityForMonth(
        date.getFullYear(),
        date.getMonth() + 1
      );
      setMonthData(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadDate = async (date) => {
    setLoading(true);
    try {
      const result = await apiGet(
        apiPath.getDriverAvailabilityCalendarDateWise,
        { date, type: selectedTab, keyword: searchQuery }
      );
      const response = result?.data?.results;
      console.log("responseresponse", response)
      setDrivers(response);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  console.log("selectedDate",selectedDate)
  useEffect(() => {
    loadDate(selectedDate);
  }, [selectedDate, selectedTab, searchKeyword]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchKeyword(searchQuery);
    }, 500);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchQuery]);

  function goMonth(delta) {
    setVisibleMonth((prev) => addMonths(prev, delta));
  }

  const monthStart = startOfMonth(visibleMonth);
  const monthEnd = endOfMonth(visibleMonth);
  const firstDayOfWeek = new Date(monthStart).getDay();

  const days = [];
  for (let i = 0; i < firstDayOfWeek; i++) {
    const d = new Date(monthStart);
    d.setDate(d.getDate() - firstDayOfWeek + i);
    days.push({ date: d, inMonth: false });
  }
  for (let d = 1; d <= monthEnd.getDate(); d++) {
    days.push({
      date: new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), d),
      inMonth: true,
    });
  }
  while (days.length % 7 !== 0) {
    const last = days[days.length - 1].date;
    const d = new Date(last);
    d.setDate(d.getDate() + 1);
    days.push({ date: d, inMonth: false });
  }

  function getDayData(date) {
    const iso = formatISODate(date);
    return (
      monthData &&
      monthData.days &&
      monthData?.days?.find((date) => {
        return date?.date == String(iso);
      })
    );
  }

  function isPast(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d < today;
  }

  return (
    <div className=" mx-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex active text-black dark:text-white">
            <Link aria-current="page" className="" to={"/driver-manager"}>
              <FaCircleArrowLeft size={27} />
            </Link>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="px-3 py-1 text-black dark:text-white" onClick={() => goMonth(-1)}>
            <MdKeyboardArrowLeft size={27} />
          </button>
          <div className="px-4 py-2 rounded text-center">
            <div className="font-medium text-black dark:text-white">
              {visibleMonth.toLocaleString(undefined, { month: "long" })}{" "}
              {visibleMonth.getFullYear()}
            </div>
          </div>
          <button className="px-3 py-1 text-black dark:text-white" onClick={() => goMonth(1)}>
            <MdKeyboardArrowRight size={27} />
          </button>
          <button
            className="ml-3 px-3 py-1 rounded bg-black text-white"
            onClick={() => {
              setVisibleMonth(startOfMonth(today));
            }}
          >
            {t("TODAY")}
          </button>
        </div>
      </div>

      <div className="bg-white border rounded shadow-sm p-4">
        <div className="grid grid-cols-7 gap-2 text-sm text-center font-medium mb-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="py-1">
              {d}
            </div>
          ))}
        </div>

        {loading && (
          <div className="py-8 text-center">{t("LOADING_MONTH")}</div>
        )}

        {!loading && (
          <div className="grid grid-cols-7 gap-2">
            {days.map(({ date, inMonth }) => {
              const iso = formatISODate(date);
              const dayData = getDayData(date);
              const available = dayData ? dayData.availableCount : 0;
              const unavailable = dayData ? dayData.unavailableCount : 0;
              const past = isPast(date);

              return (
                <div
                  key={iso}
                  className={`border rounded p-2 min-h-[96px] flex flex-col justify-between ${
                    inMonth ? "bg-white" : "bg-gray-50 text-gray-400"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div
                      className={`text-sm font-semibold ${
                        date.getTime() === today.getTime()
                          ? "text-blue-600"
                          : ""
                      }`}
                    >
                      {date.getDate()}
                    </div>
                    <div className="text-xs text-gray-500">
                      {inMonth ? "" : " "}
                    </div>
                  </div>

                  <div className="mt-2 flex flex-col gap-2">
                    {/* Available badge */}
                    <button
                      disabled={!inMonth}
                      onClick={() => {
                        if (inMonth) {
                          setSelectedDate(iso);
                          setSelectedTab("available");
                          setSearchQuery("");
                        }
                      }}
                      className={`flex items-center justify-between px-2 py-1 rounded ${
                        past ? "opacity-60 cursor-pointer" : "cursor-pointer"
                      } ${
                        available > 0
                          ? "border border-green-600"
                          : "border-gray-200"
                      }`}
                    >
                      <span className="text-xs">{t("AVAILABLE")}</span>
                      <span
                        className={`ml-2 text-sm font-semibold ${
                          available > 0 ? "text-green-700" : "text-gray-400"
                        }`}
                      >
                        {available}
                      </span>
                    </button>

                    {/* Unavailable badge */}
                    <button
                      disabled={!inMonth}
                      onClick={() => {
                        if (inMonth) {
                          setSelectedDate(iso);
                          setSelectedTab("unavailable");
                          setSearchQuery("");
                        }
                      }}
                      className={`flex items-center justify-between px-2 py-1 rounded ${
                        past ? "opacity-60 cursor-pointer" : "cursor-pointer"
                      } ${
                        unavailable > 0
                          ? "border border-red-600"
                          : "border-gray-200"
                      }`}
                    >
                      <span className="text-xs">{t("UNAVAILABLE")}</span>
                      <span
                        className={`ml-2 text-sm font-semibold ${
                          unavailable > 0 ? "text-red-700" : "text-gray-400"
                        }`}
                      >
                        {unavailable}
                      </span>
                    </button>
                    <button
                      disabled={!inMonth}
                      onClick={() => {
                        if (inMonth) {
                          setSelectedDate(iso);
                          setSelectedTab("all");
                          setSearchQuery("");
                        }
                      }}
                      className={`mt-1 text-xs px-2 py-1 rounded border border-gray-200 ${
                        past ? "opacity-60" : ""
                      }`}
                    >
                      {t("O_ALL")}
                    </button>
                  </div>

                  <div className="text-[11px] text-gray-500 mt-2 text-right">
                    {inMonth
                      ? ""
                      : date.toLocaleString(undefined, { month: "short" })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {selectedDate && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div
            className="absolute inset-0 bg-black bg-opacity-40"
            onClick={() => handleReset()}
          ></div>

          <div className="relative bg-white w-[90%] md:w-1/3 lg:w-1/4 rounded-l-xl shadow-lg p-5 z-10">
            {/* Header */}
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">
                {new Date(selectedDate).toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "numeric",
                  day: "numeric",
                  year: "numeric",
                })}
              </h3>
              <button
                onClick={() => setSelectedDate(null)}
                className="text-gray-600 hover:text-black"
              >
                <RxCross2 size={22} />
              </button>
            </div>

            {/* Availability Summary */}
            <div className="flex items-center gap-4 mt-2 text-sm">
              <div className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                <span>
                  {t("AVAILABLE")}
                  {": "}
                  {getDayData(new Date(selectedDate))?.availableCount ?? 0}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-red-500"></span>
                <span>
                  {t("UNAVAILABLE")}
                  {": "}
                  {getDayData(new Date(selectedDate))?.unavailableCount ?? 0}
                </span>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex mt-4 h-[38px] border rounded-lg overflow-hidden text-sm font-medium">
              <button
                className={`flex-1 py-1.5 ${
                  selectedTab === "all"
                    ? "bg-black text-white"
                    : "bg-white text-black"
                }`}
                onClick={() => setSelectedTab("all")}
              >
                {t("O_ALL")}
              </button>
              <button
                className={`flex-1 py-1.5 ${
                  selectedTab === "available"
                    ? "bg-black text-white"
                    : "bg-white text-black"
                }`}
                onClick={() => setSelectedTab("available")}
              >
                {t("AVAILABLE")}
              </button>
              <button
                className={`flex-1 py-1.5 ${
                  selectedTab === "unavailable"
                    ? "bg-black text-white"
                    : "bg-white text-black"
                }`}
                onClick={() => setSelectedTab("unavailable")}
              >
                {t("UNAVAILABLE")}
              </button>
            </div>

            {/* Search */}
            <div className="mt-3 flex items-center gap-2">
              <OSearch
                placeholders={t("SEARCH_BY_NAME_EMAIL_MOBILE")}
                onChange={(e) => {
                  setSearchQuery(e?.target?.value);
                }}
                className=" h-[38px] rounded-lg"
                ref={childRef}
              />
            </div>

            {/* Driver List */}
            {console.log("drivers",drivers)}
            <div className="mt-4 pb-4 space-y-3 h-[85%] overflow-y-auto ">
              {drivers?.length ?  drivers.map((driver) => (
                <div
                  key={driver._id}
                  className="border rounded-lg p-3 shadow-sm hover:shadow-md transition cursor-pointer"
                  onClick={() => {
                    navigate("/driver-manager/view", {
                      state: { ...driver, from: "calendar" },
                    });
                  }}
                >
                  <div className="flex justify-between items-center">
                    <h4 className="text-blue-600 font-medium hover:underline">
                      {driver.driverName}
                    </h4>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        driver.availabilityStatus === "available"
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {startCase(driver.availabilityStatus)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">{driver.mobileNumber}</p>
                  <p className="text-sm text-gray-500">{driver.email}</p>
                  <div className="mt-1 text-sm text-gray-600">
                    <p>
                      <span className="font-medium">
                        {t("ASSIGNED_VEHICLE")}:
                      </span>{" "}
                      {driver?.vehicleNumber || "N/A"}
                    </p>
                  </div>
                </div>
              )): <div className="text-center">{t("O_NO_RECORD_FOUND")}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

async function fetchAvailabilityForMonth(year, month) {
  const result = await apiGet(apiPath.getDriverAvailabilityCalendar, {
    month,
    year,
  });
  const response = result?.data?.results;

  return response;
}
